import { onScopeDispose, readonly, ref } from 'vue'

import { createAmplitudeNormalizer, rmsToDbfs } from '../utils/amplitude-normalizer'
import { buildWaveformPeaks } from '../utils/waveform-peaks'
import { encodeWavPcm16, maxWavPcm16DurationSec, WAV_TARGET_SAMPLE_RATE } from '../utils/wav-encoder'
import { ATTACHMENT_MAX_FILE_SIZE, VOICE_NOTE_MAX_DURATION_SEC } from '../utils/constants'

import type { PcmRecorderSource, VoiceRecorderOptions, VoiceRecorderStage, VoiceRecordingResult } from '../types/voice.types'

/**
 * Chat voice-note recorder.
 *
 * Web counterpart of the Flutter `VoiceRecorderController` (`membership-flutter-app`
 * PR #687). Captures raw PCM via the Web Audio API and encodes a 16-bit mono WAV
 * on stop, with a live 0..1 waveform from the same RMS readings shaped by the
 * shared {@link createAmplitudeNormalizer} curve.
 *
 * Why WAV (not MediaRecorder): WAV/PCM plays natively on every target — desktop,
 * Android, iOS Safari/WKWebView, native AVFoundation — so a clip recorded on one
 * device plays everywhere (Opus/webm does not decode on iOS). MediaRecorder cannot
 * emit WAV on any browser, so we capture PCM ourselves. Pause/resume is just a
 * capture gate, which is more reliable across browsers than `MediaRecorder.pause`.
 *
 * All Web API access is behind injectable factories so the state machine is
 * unit-testable under jsdom (which has no `AudioContext`).
 */

export const VOICE_RECORDER_DEFAULTS = {
  amplitudeIntervalMs: 80,
  tickIntervalMs: 100,
  minDurationMs: 600,
  maxDurationMs: Math.min(VOICE_NOTE_MAX_DURATION_SEC, maxWavPcm16DurationSec(ATTACHMENT_MAX_FILE_SIZE)) * 1000,
} as const

const VOICE_MIME_TYPE = 'audio/wav'
/** ScriptProcessor frame size — 4096 samples balances latency and overhead. */
const CAPTURE_BUFFER_SIZE = 4096

function defaultRequestStream(): Promise<MediaStream> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return Promise.reject(new Error('Microphone access is not available in this environment'))
  }
  return navigator.mediaDevices.getUserMedia({ audio: true })
}

/**
 * Build a ScriptProcessor-backed PCM capture source from the live mic stream.
 * Accumulates mono Float32 frames while capturing and exposes the latest RMS for
 * the waveform poll. (ScriptProcessor is deprecated but works on every target incl.
 * iOS; AudioWorklet would avoid the main thread but needs module loading.)
 */
function defaultCreatePcmSource(stream: MediaStream): PcmRecorderSource {
  const AudioCtx =
    (globalThis as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext ??
    (globalThis as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!AudioCtx) {
    // No Web Audio — nothing to capture; recorder will discard an empty clip.
    return {
      sample: () => 0,
      setCapturing: () => {},
      stop: () => ({ samples: new Float32Array(0), sampleRate: WAV_TARGET_SAMPLE_RATE }),
      close: () => {},
    }
  }
  const context = new AudioCtx()
  // iOS creates AudioContexts in a suspended state; resume so the graph runs.
  if (context.state === 'suspended') void context.resume()

  const source = context.createMediaStreamSource(stream)
  const processor = context.createScriptProcessor(CAPTURE_BUFFER_SIZE, 1, 1)
  // Sink at zero gain: the processor only fires when connected downstream, but we
  // must not echo the mic back to the speakers.
  const sink = context.createGain()
  sink.gain.value = 0

  const frames: Float32Array[] = []
  let capturing = true
  let latestRms = 0

  processor.onaudioprocess = (event: AudioProcessingEvent) => {
    const input = event.inputBuffer.getChannelData(0)
    let sumSquares = 0
    for (let i = 0; i < input.length; i++) sumSquares += input[i] * input[i]
    latestRms = Math.sqrt(sumSquares / input.length)
    if (capturing) frames.push(new Float32Array(input))
  }

  source.connect(processor)
  processor.connect(sink)
  sink.connect(context.destination)

  return {
    sample: () => latestRms,
    setCapturing: (on: boolean) => {
      capturing = on
    },
    stop: () => {
      const total = frames.reduce((n, f) => n + f.length, 0)
      const samples = new Float32Array(total)
      let offset = 0
      for (const frame of frames) {
        samples.set(frame, offset)
        offset += frame.length
      }
      return { samples, sampleRate: context.sampleRate }
    },
    close: () => {
      try {
        source.disconnect()
        processor.disconnect()
        sink.disconnect()
      } catch {
        // best-effort: a detached node must not block teardown
      }
      return context.close()
    },
  }
}

export function useVoiceRecorder(options: VoiceRecorderOptions = {}) {
  const cfg = { ...VOICE_RECORDER_DEFAULTS, ...options }
  const requestStream = options.requestStream ?? defaultRequestStream
  const createPcmSource = options.createPcmSource ?? defaultCreatePcmSource
  const targetSampleRate = options.targetSampleRate ?? WAV_TARGET_SAMPLE_RATE
  const now = options.now ?? (() => Date.now())
  const normalize = createAmplitudeNormalizer()

  const stage = ref<VoiceRecorderStage>('idle')
  const elapsedMs = ref(0)
  const amplitudes = ref<number[]>([])
  const error = ref<string | null>(null)

  let stream: MediaStream | null = null
  let pcmSource: PcmRecorderSource | null = null

  let amplitudeTimer: ReturnType<typeof setInterval> | null = null
  let tickTimer: ReturnType<typeof setInterval> | null = null
  // Active recording time accumulates across pause/resume.
  let accumulatedMs = 0
  let segmentStart = 0

  function clearTimers() {
    if (amplitudeTimer != null) {
      clearInterval(amplitudeTimer)
      amplitudeTimer = null
    }
    if (tickTimer != null) {
      clearInterval(tickTimer)
      tickTimer = null
    }
  }

  function currentDurationMs(): number {
    const live = stage.value === 'recording' ? now() - segmentStart : 0
    return Math.min(accumulatedMs + live, cfg.maxDurationMs)
  }

  function startTimers() {
    clearTimers()
    amplitudeTimer = setInterval(() => {
      if (stage.value !== 'recording' || !pcmSource) return
      const rms = pcmSource.sample()
      amplitudes.value = [...amplitudes.value, normalize(rmsToDbfs(rms))]
    }, cfg.amplitudeIntervalMs)

    tickTimer = setInterval(() => {
      if (stage.value !== 'recording') return
      elapsedMs.value = currentDurationMs()
      if (elapsedMs.value >= cfg.maxDurationMs) {
        void stop()
      }
    }, cfg.tickIntervalMs)
  }

  function teardownMedia() {
    clearTimers()
    try {
      void pcmSource?.close()
    } catch {
      // best-effort: a stuck AudioContext must not block teardown
    }
    pcmSource = null
    if (stream) {
      for (const track of stream.getTracks()) track.stop()
    }
    stream = null
  }

  function resetBuffers() {
    amplitudes.value = []
    elapsedMs.value = 0
    accumulatedMs = 0
    segmentStart = 0
    error.value = null
  }

  async function start(): Promise<boolean> {
    if (stage.value !== 'idle') return false
    resetBuffers()
    try {
      stream = await requestStream()
      pcmSource = createPcmSource(stream)
      stage.value = 'recording'
      segmentStart = now()
      accumulatedMs = 0
      startTimers()
      return true
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to start recording'
      teardownMedia()
      stage.value = 'idle'
      return false
    }
  }

  function pause(): boolean {
    if (stage.value !== 'recording' || !pcmSource) return false
    accumulatedMs += now() - segmentStart
    pcmSource.setCapturing(false)
    stage.value = 'paused'
    elapsedMs.value = accumulatedMs
    return true
  }

  function resume(): boolean {
    if (stage.value !== 'paused' || !pcmSource) return false
    pcmSource.setCapturing(true)
    segmentStart = now()
    stage.value = 'recording'
    return true
  }

  function togglePause(): boolean {
    if (stage.value === 'paused') return resume()
    return pause()
  }

  /**
   * Stop recording and resolve the finished clip as WAV, or `null` when the clip
   * is shorter than {@link VoiceRecorderOptions.minDurationMs} (treated as an
   * accidental tap) or when nothing was captured.
   */
  function stop(): Promise<VoiceRecordingResult | null> {
    if (stage.value !== 'recording' && stage.value !== 'paused') {
      return Promise.resolve(null)
    }
    if (stage.value === 'recording') {
      accumulatedMs += now() - segmentStart
    }
    const finalDurationMs = Math.min(accumulatedMs, cfg.maxDurationMs)
    const capturedAmplitudes = [...amplitudes.value]
    const captured = pcmSource?.stop() ?? { samples: new Float32Array(0), sampleRate: targetSampleRate }
    stage.value = 'stopping'
    clearTimers()
    elapsedMs.value = finalDurationMs

    teardownMedia()
    stage.value = 'idle'
    if (finalDurationMs < cfg.minDurationMs || captured.samples.length === 0) {
      return Promise.resolve(null)
    }
    const blob = encodeWavPcm16(captured.samples, captured.sampleRate, targetSampleRate)
    return Promise.resolve({
      blob,
      mimeType: VOICE_MIME_TYPE,
      durationSec: Math.round(finalDurationMs / 100) / 10,
      amplitudes: capturedAmplitudes,
      waveformPeaks: buildWaveformPeaks(capturedAmplitudes, options.peakCount),
    })
  }

  /** Abort and discard the current recording without producing a clip. */
  function cancel(): void {
    if (stage.value === 'idle') return
    teardownMedia()
    resetBuffers()
    stage.value = 'idle'
  }

  onScopeDispose(() => {
    cancel()
  })

  return {
    stage: readonly(stage),
    elapsedMs: readonly(elapsedMs),
    amplitudes: readonly(amplitudes),
    error: readonly(error),
    start,
    pause,
    resume,
    togglePause,
    stop,
    cancel,
  }
}
